import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import {
    fwTaskCreationCopy as copy,
    endpoint,
    fwRefusalLabels,
    fwTaskTypeLabels,
} from '../../utils/constants';
import { findDuplicateSerials, parseSerialList } from '../../utils/fwHelpers';
import {
    isCancelled,
    isDisabledRefusal,
    isValidRevision,
    normalizeRevision,
    operationKey,
    probeWorkflow,
    refusalOf,
    refusalText,
    workflowModes,
} from '../../utils/fwWorkflow';
import TitleHeader from '../TitleHeader/TitleHeader';

const AUTO = '__automatic__';
const decimalId = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
const idOf = value =>
    Number.isSafeInteger(value) && value > 0 ? String(value) : decimalId(value) ? value : null;
const usable = file =>
    idOf(file?.id) && ['PAQ', 'PAC'].includes(file.family) && file.capability?.transfer_enabled === true;
const entriesOf = response => {
    if (!Array.isArray(response?.data?.data)) throw new Error('Invalid choice response');
    return response.data.data;
};
const initialForm = {
    name: '',
    fileId: '',
    apn: AUTO,
    serials: '',
    quota: '',
    restriction: 'none',
    revisions: '',
};
const importButtonStyle = {
    textTransform: 'none',
    boxShadow: 'none',
    fontFamily: 'inherit',
    fontWeight: 400,
    letterSpacing: 0,
    lineHeight: 'normal',
    minWidth: 0,
};
const styles = {
    root: { margin: '1rem 2rem', maxWidth: '48rem' },
    form: { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
    actions: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
    table: { overflowX: 'auto', margin: '1rem 0' },
    importButton: { alignSelf: 'flex-start' },
    fileInput: { display: 'none' },
    fileButtonIcon: { marginRight: '0.5rem' },
};

const FwWorkflowTaskForm = ({ classes }) => {
    const history = useHistory();
    const [form, setForm] = useState(initialForm);
    const [files, setFiles] = useState([]);
    const [apns, setApns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filesError, setFilesError] = useState(null);
    const [apnsError, setApnsError] = useState(null);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [entryErrors, setEntryErrors] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [phase, setPhase] = useState('editing');
    const [summary, setSummary] = useState(null);
    const [abandonOpen, setAbandonOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const lifetime = useRef({ active: false, generation: 0 });
    const readSequence = useRef(0);
    const readSource = useRef(null);
    const owner = useRef(null);
    const pending = useRef(null);
    const reader = useRef(null);
    const serialRevision = useRef(0);
    const importSequence = useRef(0);
    const importInput = useRef(null);
    const cancelButton = useRef(null);
    const active = generation => lifetime.current.active && generation === lifetime.current.generation;
    const selectedFile = files.find(file => idOf(file?.id) === form.fileId);
    const locked = phase !== 'editing';
    const invalidateImport = () => {
        importSequence.current += 1;
        reader.current?.abort();
        reader.current = null;
        setImporting(false);
    };
    const change = (field, value) => {
        if (locked || owner.current) return;
        if (field === 'serials') {
            serialRevision.current += 1;
            invalidateImport();
            setDuplicates([]);
        }
        setForm(previous => ({ ...previous, [field]: value }));
        setErrors(previous => ({ ...previous, [field]: null }));
        setEntryErrors([]);
    };
    const removeDuplicates = () => {
        if (locked || owner.current) return;
        const seen = new Set();
        const kept = parseSerialList(form.serials).filter(value => {
            const canonical = value.toUpperCase();
            if (seen.has(canonical)) return false;
            seen.add(canonical);
            return true;
        });
        setForm(previous => ({ ...previous, serials: kept.join('\n') }));
        setDuplicates([]);
        setErrors(previous => {
            const next = { ...previous };
            delete next.serials;
            return next;
        });
    };
    const removeConflicts = () => {
        if (locked || owner.current) return;
        const rejected = new Set(
            entryErrors
                .map(row =>
                    String(row.serial ?? row.value ?? '')
                        .trim()
                        .toUpperCase()
                )
                .filter(Boolean)
        );
        setForm(previous => ({
            ...previous,
            serials: parseSerialList(previous.serials)
                .filter(value => !rejected.has(value.toUpperCase()))
                .join('\n'),
        }));
        setEntryErrors([]);
    };
    const loadChoices = async (preflight = true) => {
        if (pending.current || owner.current) return;
        const generation = lifetime.current.generation;
        const sequence = ++readSequence.current;
        readSource.current?.cancel();
        readSource.current = cancelToken.source();
        const config = { cancelToken: readSource.current.token };
        const current = () => active(generation) && readSequence.current === sequence;
        setLoading(true);
        setFilesError(null);
        setApnsError(null);
        if (preflight) {
            const probe = await probeWorkflow(config);
            if (!current()) return;
            if (probe.mode !== workflowModes.CONFIGURED) {
                setFiles([]);
                setApns([]);
                setFilesError(refusalText(probe.refusal, copy.probeError));
                setLoading(false);
                return;
            }
        }
        await Promise.all([
            axiosInstance
                .get(endpoint.FW_FILES, { ...config, params: { per_page: 20 } })
                .then(response => {
                    const rows = entriesOf(response);
                    if (!current()) return;
                    setFiles(rows.filter(row => row && typeof row === 'object'));
                    setForm(previous => ({
                        ...previous,
                        fileId: rows.some(row => idOf(row?.id) === previous.fileId && usable(row))
                            ? previous.fileId
                            : '',
                    }));
                })
                .catch(problem => {
                    if (!current() || isCancelled(problem)) return;
                    setFiles([]);
                    setFilesError(copy.filesError);
                }),
            axiosInstance
                .get(endpoint.FW_APNS, { ...config, params: { per_page: 200 } })
                .then(response => {
                    const rows = entriesOf(response).filter(
                        row => typeof row?.apn === 'string' && row.apn.length > 0 && row.apn.length <= 36
                    );
                    if (!current()) return;
                    setApns(rows);
                    setForm(previous => ({
                        ...previous,
                        apn: rows.some(row => row.apn === previous.apn) ? previous.apn : AUTO,
                    }));
                })
                .catch(problem => {
                    if (!current() || isCancelled(problem)) return;
                    setApns([]);
                    setForm(previous => ({ ...previous, apn: AUTO }));
                    setApnsError(copy.apnsError);
                }),
        ]);
        if (current()) setLoading(false);
    };
    useEffect(() => {
        lifetime.current.active = true;
        lifetime.current.generation += 1;
        loadChoices(false);
        return () => {
            lifetime.current.active = false;
            lifetime.current.generation += 1;
            readSequence.current += 1;
            readSource.current?.cancel();
            owner.current?.source.cancel();
            owner.current = null;
            pending.current = null;
            importSequence.current += 1;
            reader.current?.abort();
            reader.current = null;
        };
    }, []);

    const importFile = event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || locked || owner.current) return;
        invalidateImport();
        const generation = lifetime.current.generation;
        const sequence = ++importSequence.current;
        const revision = serialRevision.current;
        const fileReader = new FileReader();
        reader.current = fileReader;
        setImporting(true);
        const current = () =>
            active(generation) &&
            sequence === importSequence.current &&
            revision === serialRevision.current &&
            !pending.current &&
            !owner.current;
        fileReader.onload = () => {
            if (!current()) return;
            const imported = parseSerialList(fileReader.result);
            serialRevision.current += 1;
            setForm(previous => ({
                ...previous,
                serials: [...parseSerialList(previous.serials), ...imported].join('\n'),
            }));
            reader.current = null;
            setImporting(false);
        };
        fileReader.onerror = () => {
            if (!current()) return;
            setError(copy.importError);
            reader.current = null;
            setImporting(false);
        };
        fileReader.readAsText(file);
    };

    const prepare = () => {
        if (locked || owner.current || loading || filesError || importing) return;
        const invalid = {};
        const name = form.name.trim();
        const serials = parseSerialList(form.serials).map(value => value.toUpperCase());
        const revisions = parseSerialList(form.revisions).map(normalizeRevision);
        const quota = Number(form.quota);
        if (!name || Array.from(name).length > 255) invalid.name = copy.invalidName;
        if (!usable(selectedFile)) invalid.fileId = copy.invalidFile;
        const duplicateValues = findDuplicateSerials(serials);
        if (!serials.length || serials.length > 10000 || serials.some(value => !/^[0-9A-F]{8}$/.test(value)))
            invalid.serials = copy.invalidSerials;
        if (!/^\d+$/.test(form.quota) || !Number.isSafeInteger(quota) || quota < 1 || quota > serials.length)
            invalid.quota = copy.invalidQuota;
        if (form.apn !== AUTO && !apns.some(row => row.apn === form.apn)) invalid.apn = copy.invalidApn;
        if (
            form.restriction !== 'none' &&
            (!revisions.length || revisions.length > 1000 || revisions.some(value => !isValidRevision(value)))
        )
            invalid.revisions = copy.invalidRevisions;
        setErrors(invalid);
        setError(null);
        setEntryErrors([]);
        setDuplicates(duplicateValues);
        if (Object.keys(invalid).length || duplicateValues.length) return;
        const fileId = idOf(selectedFile.id);
        const payload = {
            name,
            fw_file_id: Number.isSafeInteger(Number(fileId)) ? Number(fileId) : fileId,
            firmware_type: selectedFile.family === 'PAQ' ? 1 : 2,
            serial_numbers: serials,
            allowance: quota,
            route_mode: form.apn === AUTO ? 'dynamic' : 'static',
        };
        if (form.apn !== AUTO) payload.apn = form.apn;
        if (form.restriction !== 'none') {
            payload.revision_restriction_type = form.restriction;
            payload.revision_restrictions = revisions;
        }
        pending.current = { payload, key: operationKey() };
        setSummary({ filename: selectedFile.filename, family: selectedFile.family, ...payload });
        setPhase('confirming');
    };
    const send = async () => {
        if (owner.current || !pending.current || !['confirming', 'uncertain'].includes(phase)) return;
        const operation = pending.current;
        const request = { generation: lifetime.current.generation, source: cancelToken.source() };
        owner.current = request;
        const current = () =>
            owner.current === request && active(request.generation) && pending.current === operation;
        setPhase('sending');
        setError(null);
        let dispatched = false;
        try {
            const probe = await probeWorkflow({ cancelToken: request.source.token });
            if (!current()) return;
            if (probe.mode !== workflowModes.CONFIGURED) {
                setError(refusalText(probe.refusal, copy.probeError));
                setPhase('uncertain');
                return;
            }
            dispatched = true;
            const response = await axiosInstance.post(endpoint.FW_TASKS, operation.payload, {
                headers: { 'Idempotency-Key': operation.key },
                cancelToken: request.source.token,
            });
            if (!current()) return;
            const result = response?.data;
            if (
                result?.code !== 'FW_OPERATION_RECORDED' ||
                !decimalId(result?.data?.task_id) ||
                !decimalId(result?.data?.quota_cycle_id) ||
                result.data.version !== 1 ||
                !decimalId(result.operation_id)
            ) {
                setError(copy.unknown);
                setPhase('blocked');
                return;
            }
            pending.current = null;
            owner.current = null;
            setSummary(null);
            history.push(`/fw-task/${result.data.task_id}`);
        } catch (problem) {
            if (!current() || isCancelled(problem)) return;
            const refusal = refusalOf(problem);
            setError(
                refusalText(
                    refusal,
                    dispatched && (refusal.status == null || refusal.status >= 500)
                        ? copy.uncertain
                        : copy.refused
                )
            );
            if (!dispatched || refusal.status == null || refusal.status >= 500) {
                setPhase('uncertain');
            } else if (refusal.status === 422 && !isDisabledRefusal(refusal)) {
                const fields = refusal.fields || {};
                const map = {
                    name: 'name',
                    fw_file_id: 'fileId',
                    serial_numbers: 'serials',
                    allowance: 'quota',
                    apn: 'apn',
                    revision_restrictions: 'revisions',
                    revision: 'revisions',
                };
                const nextErrors = {};
                Object.entries(fields).forEach(([field, messages]) => {
                    if (field === 'entries') return;
                    const value = Array.isArray(messages)
                        ? messages.find(item => typeof item === 'string')
                        : messages;
                    if (typeof value === 'string')
                        nextErrors[map[field.split('.')[0]] || field] = fwRefusalLabels[value] || value;
                });
                setErrors(nextErrors);
                setEntryErrors(
                    Array.isArray(fields.entries)
                        ? fields.entries.filter(row => row && typeof row === 'object')
                        : []
                );
                pending.current = null;
                setSummary(null);
                setPhase('editing');
            } else {
                setPhase('blocked');
            }
        } finally {
            if (owner.current === request) owner.current = null;
        }
    };
    const dismiss = () => {
        if (owner.current) return;
        if (phase === 'confirming') {
            pending.current = null;
            setSummary(null);
            setPhase('editing');
        }
    };
    const abandon = () => {
        if (owner.current) return;
        lifetime.current.generation += 1;
        invalidateImport();
        pending.current = null;
        setSummary(null);
        setPhase('editing');
        setError(null);
        setAbandonOpen(false);
    };
    const helper = field => errors[field] || undefined;
    return (
        <div className={classes.root}>
            <TitleHeader title={copy.title} />
            <Typography>{copy.windows}</Typography>
            {filesError && <Typography role="alert">{filesError}</Typography>}
            {apnsError && <Typography role="alert">{apnsError}</Typography>}
            {error && !summary && <Typography role="alert">{error}</Typography>}
            <div className={classes.actions}>
                <Button onClick={() => loadChoices()} disabled={locked || loading || importing}>
                    {copy.refresh}
                </Button>
            </div>
            <form
                className={classes.form}
                noValidate
                onSubmit={event => {
                    event.preventDefault();
                    prepare();
                }}>
                <TextField
                    id="fw-create-name"
                    label={copy.name}
                    value={form.name}
                    onChange={event => change('name', event.target.value)}
                    disabled={locked}
                    error={!!errors.name}
                    helperText={helper('name')}
                    variant="outlined"
                    required
                />
                <FormControl
                    variant="outlined"
                    disabled={locked || loading || !!filesError}
                    error={!!errors.fileId}>
                    <InputLabel id="fw-create-file-label">{copy.file}</InputLabel>
                    <Select
                        labelId="fw-create-file-label"
                        id="fw-create-file"
                        label={copy.file}
                        value={form.fileId}
                        onChange={event => change('fileId', event.target.value)}>
                        {!files.length && (
                            <MenuItem value="" disabled>
                                {copy.noFiles}
                            </MenuItem>
                        )}
                        {files.map((file, index) => (
                            <MenuItem
                                key={idOf(file.id) || index}
                                value={idOf(file.id) || ''}
                                disabled={!usable(file)}>
                                {file.filename}{' '}
                                {!usable(file) ? `— ${file.capability?.reason || copy.unavailable}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.fileId && <Typography role="alert">{errors.fileId}</Typography>}
                </FormControl>
                <Typography>
                    {copy.family}:{' '}
                    {usable(selectedFile) ? fwTaskTypeLabels[selectedFile.family === 'PAQ' ? 1 : 2] : '—'}
                </Typography>
                <FormControl variant="outlined" disabled={locked} error={!!errors.apn}>
                    <InputLabel id="fw-create-apn-label">{copy.apn}</InputLabel>
                    <Select
                        labelId="fw-create-apn-label"
                        id="fw-create-apn"
                        label={copy.apn}
                        value={form.apn}
                        onChange={event => change('apn', event.target.value)}>
                        <MenuItem value={AUTO}>{copy.automatic}</MenuItem>
                        {apns.map(row => (
                            <MenuItem key={row.apn} value={row.apn}>
                                {row.apn}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.apn && <Typography role="alert">{errors.apn}</Typography>}
                </FormControl>
                <Typography variant="caption">{copy.routing}</Typography>
                <TextField
                    id="fw-create-serials"
                    label={copy.serials}
                    value={form.serials}
                    onChange={event => change('serials', event.target.value)}
                    disabled={locked}
                    multiline
                    minRows={5}
                    error={!!errors.serials}
                    helperText={helper('serials') || copy.serialHint}
                    variant="outlined"
                />
                <input
                    ref={importInput}
                    id="fw-create-import"
                    type="file"
                    accept=".csv,.txt"
                    aria-label={copy.import}
                    disabled={locked || importing}
                    onChange={importFile}
                    className={classes.fileInput}
                />
                <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={locked || importing}
                    onClick={() => importInput.current?.click()}
                    className={classes.importButton}
                    style={importButtonStyle}>
                    <CloudUploadIcon className={classes.fileButtonIcon} />
                    {copy.import}
                </Button>
                <TextField
                    id="fw-create-quota"
                    label={copy.quota}
                    value={form.quota}
                    onChange={event => change('quota', event.target.value)}
                    disabled={locked}
                    error={!!errors.quota}
                    helperText={helper('quota') || copy.quotaHint}
                    variant="outlined"
                    inputProps={{ inputMode: 'numeric' }}
                    required
                />
                <FormControl variant="outlined" disabled={locked}>
                    <InputLabel id="fw-create-restriction-label">{copy.restriction}</InputLabel>
                    <Select
                        labelId="fw-create-restriction-label"
                        id="fw-create-restriction"
                        label={copy.restriction}
                        value={form.restriction}
                        onChange={event => change('restriction', event.target.value)}>
                        <MenuItem value="none">{copy.none}</MenuItem>
                        <MenuItem value="allow">{copy.allow}</MenuItem>
                        <MenuItem value="deny">{copy.deny}</MenuItem>
                    </Select>
                </FormControl>
                {form.restriction !== 'none' && (
                    <TextField
                        id="fw-create-revisions"
                        label={copy.revisions}
                        value={form.revisions}
                        onChange={event => change('revisions', event.target.value)}
                        disabled={locked}
                        error={!!errors.revisions}
                        helperText={helper('revisions') || copy.revisionHint}
                        variant="outlined"
                    />
                )}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={locked || loading || !!filesError || importing}>
                    {copy.create}
                </Button>
            </form>
            {duplicates.length > 0 && (
                <div className={classes.table}>
                    <Typography role="alert">
                        {copy.duplicatesTitle}: {duplicates.length}. {copy.duplicatesHint}
                    </Typography>
                    <Typography>{duplicates.join(', ')}</Typography>
                    <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        onClick={removeDuplicates}
                        disabled={locked}>
                        {copy.removeDuplicates}
                    </Button>
                </div>
            )}
            {entryErrors.length > 0 && (
                <div className={classes.table}>
                    <Typography role="alert">
                        {copy.entries}: {copy.entriesTotal} {entryErrors.length}. {copy.entriesWindow}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{copy.entryIndex}</TableCell>
                                <TableCell>{copy.entryValue}</TableCell>
                                <TableCell>{copy.entryCode}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entryErrors.slice(0, 20).map(row => (
                                <TableRow key={`${row.index}-${row.code}-${row.value}`}>
                                    <TableCell>
                                        {Number.isSafeInteger(row.index) ? row.index + 1 : '—'}
                                    </TableCell>
                                    <TableCell>{typeof row.value === 'string' ? row.value : '—'}</TableCell>
                                    <TableCell>
                                        {typeof row.code === 'string' ? row.code : copy.error}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        onClick={removeConflicts}
                        disabled={locked}>
                        {copy.removeConflicts}
                    </Button>
                </div>
            )}
            <Dialog
                open={!!summary}
                onClose={dismiss}
                aria-labelledby="fw-create-dialog-title"
                TransitionProps={{ onEntered: () => cancelButton.current?.focus() }}
                fullWidth
                maxWidth="sm">
                <DialogTitle id="fw-create-dialog-title">{copy.confirmTitle}</DialogTitle>
                <DialogContent dividers>
                    <Typography>{summary?.name}</Typography>
                    <Typography>
                        {copy.file}: {summary?.filename}
                    </Typography>
                    <Typography>
                        {copy.family}: {summary?.family}
                    </Typography>
                    <Typography>
                        {copy.serialCount}: {summary?.serial_numbers.length}
                    </Typography>
                    <Typography>
                        {copy.quota}: {summary?.allowance}
                    </Typography>
                    <Typography>
                        {copy.apn}: {summary?.apn || copy.automatic}
                    </Typography>
                    <Typography>
                        {copy.restriction}: {summary?.revision_restriction_type || copy.none}{' '}
                        {summary?.revision_restrictions?.join(', ')}
                    </Typography>
                    <Typography>{copy.effect}</Typography>
                    {phase === 'sending' && <Typography role="status">{copy.sending}</Typography>}
                    {phase === 'uncertain' && <Typography>{copy.uncertain}</Typography>}
                    {error && <Typography role="alert">{error}</Typography>}
                </DialogContent>
                <DialogActions>
                    {phase === 'confirming' && (
                        <>
                            <Button ref={cancelButton} onClick={dismiss}>
                                {copy.cancel}
                            </Button>
                            <Button onClick={send} color="primary">
                                {copy.confirm}
                            </Button>
                        </>
                    )}
                    {phase === 'uncertain' && <Button onClick={send}>{copy.retry}</Button>}
                    {['uncertain', 'blocked'].includes(phase) && (
                        <Button onClick={() => setAbandonOpen(true)}>{copy.edit}</Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog
                open={abandonOpen}
                onClose={() => setAbandonOpen(false)}
                aria-labelledby="fw-create-abandon-title">
                <DialogTitle id="fw-create-abandon-title">{copy.abandonTitle}</DialogTitle>
                <DialogContent>
                    <Typography>{copy.abandonWarning}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAbandonOpen(false)}>{copy.cancel}</Button>
                    <Button onClick={abandon}>{copy.abandonConfirm}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withStyles(styles)(FwWorkflowTaskForm);
